import { api } from "@/app/services/api.service";

export const SIMPO_API_KEY_PATH = "integration/api/simpo/apikey";
export const SIMPO_CREDENTIALS_PATH = "integration/api/simpo/credentials";

export type SimpoApiKey = {
  apiKey: string | null;
  configured: boolean;
};

export type SimpoCredentials = {
  consumerKey: string | null;
  consumerSecret: string | null;
  configured: boolean;
};

export function unwrapPayload(res: { data: unknown }) {
  const data = res.data as Record<string, unknown>;
  if (
    data != null &&
    typeof data === "object" &&
    Object.prototype.hasOwnProperty.call(data, "data") &&
    data.data != null
  ) {
    return data.data;
  }
  return data;
}

export function extractErrorMessage(error: unknown, fallback: string) {
  const err = error as { response?: { data?: { message?: string } } };
  return err.response?.data?.message || fallback;
}

function readString(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (value != null && value !== "") return String(value);
  }
  return null;
}

export function parseSimpoApiKey(body: unknown): SimpoApiKey {
  // GET returns { data: "*******" } — data is a plain string
  if (typeof body === "string") {
    const apiKey = body.trim();
    return { apiKey: apiKey || null, configured: apiKey.length > 0 };
  }
  if (body == null || typeof body !== "object") {
    return { apiKey: null, configured: false };
  }
  const record = body as Record<string, unknown>;
  const apiKey = readString(record, ["apiKey", "api_key", "key"]);
  const configured =
    record.configured === true ||
    record.hasApiKey === true ||
    (apiKey != null && apiKey.length > 0);

  return { apiKey, configured };
}

export function parseSimpoCredentials(body: unknown): SimpoCredentials {
  if (body == null || typeof body !== "object") {
    return { consumerKey: null, consumerSecret: null, configured: false };
  }
  const record = body as Record<string, unknown>;
  const consumerKey = readString(record, ["consumerKey", "consumer_key", "key"]);
  const consumerSecret = readString(record, ["consumerSecret", "consumer_secret", "secret"]);
  const configured =
    record.configured === true ||
    record.hasCredentials === true ||
    (consumerKey != null && consumerKey.length > 0);

  return { consumerKey, consumerSecret, configured };
}

export async function fetchSimpoApiKey(): Promise<SimpoApiKey> {
  const res = await api().get(SIMPO_API_KEY_PATH);
  return parseSimpoApiKey(unwrapPayload(res));
}

export async function saveSimpoApiKey(payload: { apiKey: string }) {
  await api().post(SIMPO_API_KEY_PATH, payload);
}

export async function fetchSimpoCredentials(): Promise<SimpoCredentials> {
  const res = await api().get(SIMPO_CREDENTIALS_PATH);
  return parseSimpoCredentials(unwrapPayload(res));
}

export async function saveSimpoCredentials(payload: {
  consumerKey: string;
  consumerSecret: string;
}) {
  await api().post(SIMPO_CREDENTIALS_PATH, payload);
}
